// ABOUTME: Usage analytics component with charts and metrics.
// ABOUTME: Displays workspace usage trends, event breakdown, and top users.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  MessageSquare,
  Search,
  Coins,
  Zap,
  TrendingUp,
  Users,
  Cpu,
  Filter,
  ChevronDown,
  X,
} from 'lucide-react';
import { workspaceApi } from '@/services/api/adminClient';
import type { UsageAnalyticsResponse, UsageFeatureType } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface UsageAnalyticsProps {
  workspaceId: string;
}

const COLORS = ['#6366f1', '#14b8a6', '#f59e0b', '#f43f5e', '#8b5cf6'];

// Feature filter options
const FEATURE_OPTIONS: { value: UsageFeatureType; label: string }[] = [
  { value: 'investigation', label: 'Investigations' },
  { value: 'report_generation', label: 'Report Generation' },
  { value: 'magic_edit', label: 'Magic Edit' },
];

// Token type filter options
type TokenTypeFilter = 'all' | 'input' | 'output';
const TOKEN_TYPE_OPTIONS: { value: TokenTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Tokens' },
  { value: 'input', label: 'Input Tokens' },
  { value: 'output', label: 'Output Tokens' },
];

export function UsageAnalytics({ workspaceId }: UsageAnalyticsProps) {
  const [data, setData] = useState<UsageAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  // Model breakdown filters
  const [selectedFeatures, setSelectedFeatures] = useState<UsageFeatureType[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [tokenTypeFilter, setTokenTypeFilter] = useState<TokenTypeFilter>('all');
  const [featureDropdownOpen, setFeatureDropdownOpen] = useState(false);
  const featureDropdownRef = useRef<HTMLDivElement>(null);

  // Cache all available models from initial unfiltered load
  const [allModels, setAllModels] = useState<{ model: string; provider: string }[]>([]);
  const [tableLoading, setTableLoading] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        featureDropdownRef.current &&
        !featureDropdownRef.current.contains(event.target as Node)
      ) {
        setFeatureDropdownOpen(false);
      }
    }

    if (featureDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [featureDropdownOpen]);

  // Track if this is the initial load vs filter change
  const isInitialLoad = useRef(true);

  // Unified data loading effect
  useEffect(() => {
    const hasFilters = selectedFeatures.length > 0 || !!selectedModel;

    async function loadData() {
      try {
        // Use full page loading for initial load, table loading for filter changes
        if (isInitialLoad.current) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }

        const response = await workspaceApi.getUsage(workspaceId, {
          days,
          feature: selectedFeatures.length > 0 ? selectedFeatures : undefined,
          model: selectedModel || undefined,
        });
        setData(response);
        setError(null); // Clear any previous errors

        // Cache all models from unfiltered response (only on initial load or when filters are cleared)
        if (!hasFilters) {
          setAllModels(
            response.model_breakdown.map((m) => ({ model: m.model, provider: m.provider }))
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load usage data');
      } finally {
        setLoading(false);
        setTableLoading(false);
        isInitialLoad.current = false;
      }
    }
    loadData();
  }, [workspaceId, days, selectedFeatures, selectedModel]);

  // Transform trend data for charts (memoized) - must be before early returns
  const trendData = useMemo(
    () =>
      data?.trend
        ? data.trend.labels.map((label, index) => ({
            date: label,
            messages: data.trend.messages[index],
            investigations: data.trend.investigations[index],
            tokens: Math.round(data.trend.tokens[index] / 1000), // Convert to K
            queries: data.trend.queries[index],
            cost: data.trend.costs[index],
          }))
        : [],
    [data?.trend]
  );

  // Transform filtered token trend data for charts (memoized) - must be before early returns
  const filteredTokenTrendData = useMemo(
    () =>
      data?.filtered_token_trend
        ? data.filtered_token_trend.labels.map((label, index) => ({
            date: label,
            input:
              tokenTypeFilter !== 'output'
                ? Math.round(data.filtered_token_trend.tokens_input[index] / 1000)
                : undefined,
            output:
              tokenTypeFilter !== 'input'
                ? Math.round(data.filtered_token_trend.tokens_output[index] / 1000)
                : undefined,
          }))
        : [],
    [data?.filtered_token_trend, tokenTypeFilter]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
        {error || 'Failed to load usage data'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-end gap-2">
        {[7, 30, 90].map((d) => (
          <Button
            key={d}
            variant={days === d ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setDays(d)}
          >
            {d} Days
          </Button>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Messages"
          value={data.summary.messages_count.toLocaleString()}
          icon={<MessageSquare className="w-5 h-5" />}
          color="indigo"
        />
        <SummaryCard
          title="Investigations"
          value={data.summary.investigations_count.toLocaleString()}
          icon={<Search className="w-5 h-5" />}
          color="teal"
        />
        <SummaryCard
          title="LLM Tokens"
          value={`${(data.summary.llm_tokens_count / 1000000).toFixed(2)}M`}
          icon={<Zap className="w-5 h-5" />}
          color="warning"
        />
        <SummaryCard
          title="Total Cost"
          value={`$${data.summary.total_cost_usd.toFixed(2)}`}
          icon={<Coins className="w-5 h-5" />}
          color="coral"
        />
      </div>

      {/* Usage Trend Chart */}
      <Card variant="surface">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-surface-500" />
          <h3 className="text-sm font-medium text-surface-800">Usage Trend</h3>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                tick={{ fill: '#6b7280', fontSize: 12 }}
              />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}
                labelStyle={{ color: '#1f2937' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="messages"
                name="Messages"
                stroke="#6366f1"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="investigations"
                name="Investigations"
                stroke="#14b8a6"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="queries"
                name="Queries"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Trend */}
        <Card variant="surface">
          <div className="mb-4 flex items-center gap-2">
            <Coins className="w-5 h-5 text-surface-500" />
            <h3 className="text-sm font-medium text-surface-800">Cost Over Time</h3>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                  }}
                  labelStyle={{ color: '#1f2937' }}
                  formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Cost']}
                />
                <Bar dataKey="cost" fill="#f43f5e" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Event Breakdown */}
        <Card variant="surface">
          <div className="mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-surface-500" />
            <h3 className="text-sm font-medium text-surface-800">Event Breakdown</h3>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.event_breakdown.map((e) => ({
                    name: e.event_type,
                    value: e.count,
                    cost: e.total_cost_usd,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) =>
                    `${name}: ${((percent || 0) * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {data.event_breakdown.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Top Users */}
      <Card variant="surface">
        <div className="mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-surface-500" />
          <h3 className="text-sm font-medium text-surface-800">Top Users</h3>
        </div>
        {data.top_users.length === 0 ? (
          <p className="text-sm text-surface-500">No user data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-500 border-b border-surface-200">
                  <th className="pb-3 font-medium">User</th>
                  <th className="pb-3 font-medium text-right">Messages</th>
                  <th className="pb-3 font-medium text-right">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.map((user) => (
                  <tr key={user.user_id} className="border-b border-surface-100">
                    <td className="py-3">
                      <div>
                        <p className="text-surface-800 font-medium">{user.name}</p>
                        <p className="text-xs text-surface-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="py-3 text-right text-surface-800">
                      {user.messages_count.toLocaleString()}
                    </td>
                    <td className="py-3 text-right text-surface-800">
                      {(user.tokens_count / 1000).toFixed(1)}K
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Filtered Token Trend Chart and Model Breakdown - Shared Filters */}
      <Card variant="surface" overflowVisible>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-surface-500" />
            <h3 className="text-sm font-medium text-surface-800">Token Usage Analysis</h3>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-surface-400" />
            <span className="text-xs text-surface-500">Filters:</span>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="mb-4 flex flex-wrap gap-3">
          {/* Feature Multi-Select */}
          <div className="relative" ref={featureDropdownRef}>
            <button
              onClick={() => setFeatureDropdownOpen(!featureDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm border border-surface-200 dark:border-surface-700 rounded-sm hover:border-surface-300 dark:hover:border-surface-600 bg-surface-0 dark:bg-surface-800"
            >
              <span className="text-surface-600 dark:text-surface-300">
                {selectedFeatures.length === 0
                  ? 'All Features'
                  : `${selectedFeatures.length} selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-surface-400" />
            </button>
            {featureDropdownOpen && (
              <div className="absolute z-10 mt-1 w-48 bg-surface-0 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-sm shadow-lg">
                {FEATURE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-surface-50 dark:hover:bg-surface-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFeatures.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFeatures([...selectedFeatures, option.value]);
                        } else {
                          setSelectedFeatures(selectedFeatures.filter((f) => f !== option.value));
                        }
                      }}
                      className="rounded border-surface-300 dark:border-surface-600 dark:bg-surface-700"
                    />
                    <span className="text-sm text-surface-700 dark:text-surface-200">{option.label}</span>
                  </label>
                ))}
                {selectedFeatures.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedFeatures([]);
                      setFeatureDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-surface-50 dark:hover:bg-surface-700 border-t border-surface-100 dark:border-surface-700"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Model Selector - uses cached models to avoid disappearing options */}
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-3 py-1.5 text-sm border border-surface-200 dark:border-surface-700 rounded-sm hover:border-surface-300 dark:hover:border-surface-600 bg-surface-0 dark:bg-surface-800 text-surface-600 dark:text-surface-300"
          >
            <option value="">All Models</option>
            {allModels.map((m) => (
              <option key={m.model} value={m.model}>
                {m.model}
              </option>
            ))}
          </select>

          {/* Token Type Selector */}
          <select
            value={tokenTypeFilter}
            onChange={(e) => setTokenTypeFilter(e.target.value as TokenTypeFilter)}
            className="px-3 py-1.5 text-sm border border-surface-200 dark:border-surface-700 rounded-sm hover:border-surface-300 dark:hover:border-surface-600 bg-surface-0 dark:bg-surface-800 text-surface-600 dark:text-surface-300"
          >
            {TOKEN_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Active Filters Display */}
          {(selectedFeatures.length > 0 || selectedModel) && (
            <div className="flex items-center gap-1">
              {selectedFeatures.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-sm"
                >
                  {FEATURE_OPTIONS.find((o) => o.value === f)?.label}
                  <button
                    onClick={() => setSelectedFeatures(selectedFeatures.filter((sf) => sf !== f))}
                    className="hover:text-primary-900 dark:hover:text-primary-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedModel && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-sm">
                  {selectedModel}
                  <button onClick={() => setSelectedModel('')} className="hover:text-teal-900 dark:hover:text-teal-100">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Filtered Token Trend Chart */}
        <div className="mb-6">
          <h4 className="text-xs font-medium text-surface-600 mb-3">Token Usage Over Time</h4>
          {tableLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={filteredTokenTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    tickFormatter={(value) => `${value}K`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                    }}
                    labelStyle={{ color: '#1f2937' }}
                    formatter={(value, name) => [
                      `${Number(value).toLocaleString()}K`,
                      name === 'input' ? 'Input Tokens' : 'Output Tokens',
                    ]}
                  />
                  <Legend />
                  {tokenTypeFilter !== 'output' && (
                    <Line
                      type="monotone"
                      dataKey="input"
                      name="Input Tokens"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                    />
                  )}
                  {tokenTypeFilter !== 'input' && (
                    <Line
                      type="monotone"
                      dataKey="output"
                      name="Output Tokens"
                      stroke="#14b8a6"
                      strokeWidth={2}
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-surface-200 my-4" />

        {/* Model Breakdown Table */}
        <h4 className="text-xs font-medium text-surface-600 mb-3">Token Usage by Model</h4>

        {tableLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : data.model_breakdown.length === 0 ? (
          <p className="text-sm text-surface-500">No model usage data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-500 border-b border-surface-200">
                  <th className="pb-3 font-medium">Model</th>
                  {(tokenTypeFilter === 'all' || tokenTypeFilter === 'input') && (
                    <th className="pb-3 font-medium text-right">Input Tokens</th>
                  )}
                  {(tokenTypeFilter === 'all' || tokenTypeFilter === 'output') && (
                    <th className="pb-3 font-medium text-right">Output Tokens</th>
                  )}
                  <th className="pb-3 font-medium text-right">Calls</th>
                  <th className="pb-3 font-medium text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.model_breakdown.map((model) => (
                  <tr
                    key={`${model.provider}-${model.model}`}
                    className="border-b border-surface-100"
                  >
                    <td className="py-3">
                      <div>
                        <p className="text-surface-800 font-medium">{model.model}</p>
                        <p className="text-xs text-surface-500">{model.provider}</p>
                      </div>
                    </td>
                    {(tokenTypeFilter === 'all' || tokenTypeFilter === 'input') && (
                      <td className="py-3 text-right text-surface-800">
                        {model.tokens_input >= 1000000
                          ? `${(model.tokens_input / 1000000).toFixed(2)}M`
                          : model.tokens_input >= 1000
                            ? `${(model.tokens_input / 1000).toFixed(1)}K`
                            : model.tokens_input.toLocaleString()}
                      </td>
                    )}
                    {(tokenTypeFilter === 'all' || tokenTypeFilter === 'output') && (
                      <td className="py-3 text-right text-surface-800">
                        {model.tokens_output >= 1000000
                          ? `${(model.tokens_output / 1000000).toFixed(2)}M`
                          : model.tokens_output >= 1000
                            ? `${(model.tokens_output / 1000).toFixed(1)}K`
                            : model.tokens_output.toLocaleString()}
                      </td>
                    )}
                    <td className="py-3 text-right text-surface-800">
                      {model.call_count.toLocaleString()}
                    </td>
                    <td className="py-3 text-right text-surface-800">
                      ${model.total_cost_usd.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'indigo' | 'teal' | 'warning' | 'coral';
}

function SummaryCard({ title, value, icon, color }: SummaryCardProps) {
  const colorClasses = {
    indigo: 'bg-primary-50 text-primary-600',
    teal: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    coral: 'bg-error-light text-error',
  };

  return (
    <Card variant="surface">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-xs text-surface-500">{title}</p>
          <p className="text-lg font-semibold text-surface-800">{value}</p>
        </div>
      </div>
    </Card>
  );
}

export default UsageAnalytics;
